"use client"
import { Box, Button, getThemeColor, type MantineColor, useMantineTheme, type ButtonProps } from "@mantine/core"
import classes from "./CTAButton.module.css"
import { motion, useAnimationFrame, useMotionTemplate, useMotionValue, useTransform } from "framer-motion"
import { useEffect, useMemo, useRef, useState } from "react"

interface CTAButtonProps extends Omit<ButtonProps, "component"> {
  href?: string
  duration?: number
  animationColor?: MantineColor
}

function MovingBorder({ children, duration = 2000, rx = "30%", ry = "30%", ...otherProps }: { children: React.ReactNode; duration?: number; rx?: string; ry?: string; [key: string]: any }) {
  const pathRef = useRef<any>(null)
  const progress = useMotionValue<number>(0)

  useAnimationFrame((time) => {
    const length = pathRef.current?.getTotalLength()
    if (length) {
      const pxPerMillisecond = length / duration
      progress.set((time * pxPerMillisecond) % length)
    }
  })

  const x = useTransform(progress, (val) => pathRef.current?.getPointAtLength(val).x)
  const y = useTransform(progress, (val) => pathRef.current?.getPointAtLength(val).y)

  const transform = useMotionTemplate`translateX(${x}px) translateY(${y}px) translateX(-50%) translateY(-50%)`

  return (
    <>
      <svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" className={classes.svg} width="100%" height="100%" {...otherProps}>
        <rect fill="none" width="100%" height="100%" rx={rx} ry={ry} ref={pathRef} />
      </svg>
      <motion.div className={classes.motionContainer} style={{ transform }}>
        {children}
      </motion.div>
    </>
  )
}

export function CTAButton({ children, href, duration, size = "sm", radius = "sm", animationColor = "red", ...props }: CTAButtonProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [resolvedRadius, setResolvedRadius] = useState("4px")
  const theme = useMantineTheme()
  const [gradientAngle, setGradientAngle] = useState(90)
  const lastTimeRef = useRef(0)

  const cssVarRadius = `var(--mantine-radius-${radius})`

  const htmlColor = useMemo(() => {
    return getThemeColor(animationColor, theme)
  }, [animationColor, theme])

  const gradientSize = useMemo(() => {
    switch (size) {
      // TODO: Configure by size
      default:
        return "4rem"
    }
  }, [size])

  useEffect(() => {
    if (containerRef.current) {
      const computed = getComputedStyle(containerRef.current).getPropertyValue(`--mantine-radius-${radius}`).trim()
      if (computed) {
        setResolvedRadius(computed)
      }
    }
  }, [radius])

  useEffect(() => {
    let animationFrameId: number
    const animate = (currentTime: number) => {
      const deltaTime = currentTime - lastTimeRef.current
      lastTimeRef.current = currentTime

      // Rotate 30 degrees per second (adjust this value to control speed)
      const rotationSpeed = 30 / 1000 // degrees per millisecond
      const angleIncrement = deltaTime * rotationSpeed

      setGradientAngle((prev) => (prev + angleIncrement) % 360)
      animationFrameId = requestAnimationFrame(animate)
    }
    lastTimeRef.current = performance.now()
    animationFrameId = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(animationFrameId)
  }, [])

  return (
    <Box className={classes.container} style={{ borderRadius: cssVarRadius }} ref={containerRef}>
      <Box className={classes.borderContainer} style={{ borderRadius: cssVarRadius }}>
        <MovingBorder duration={duration} rx={resolvedRadius} ry={resolvedRadius}>
          <Box
            className={classes.gradient}
            style={{
              "--gradient-color": htmlColor,
              "--gradient-size": gradientSize,
            }}
          />
        </MovingBorder>
      </Box>

      <Button component={"a"} href={href ?? ""} radius={radius} variant="filled" size={size} className={classes.button} {...props}>
        {children}
      </Button>
    </Box>
  )
}
